/**
 * Velocare Integration Panel
 *
 * Displays auto-populated status for supplies and RPM from Velocare integration.
 * Shows real-time status and estimated delivery times.
 */

import { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Chip,
  Stack,
  Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { addHours, addMinutes } from 'date-fns';
import { timeLabelWithZone } from '@caredna-marlin/utils/dates';

interface VelocareStatus {
  supplies: {
    status: 'pending' | 'confirmed' | 'in_transit' | 'delivered';
    estimatedDelivery: string | null;
    items: string[];
  };
  rpm: {
    status: 'pending' | 'confirmed' | 'scheduled' | 'installed';
    estimatedArrival: string | null;
  };
}

interface VelocareIntegrationPanelProps {
  onStatusChange: (status: VelocareStatus) => void;
  disabled?: boolean;
  patientTimezone: string;
}

export function VelocareIntegrationPanel({
  onStatusChange,
  disabled = false,
  patientTimezone,
}: VelocareIntegrationPanelProps) {
  const [status, setStatus] = useState<VelocareStatus>({
    supplies: {
      status: 'pending',
      estimatedDelivery: null,
      items: [],
    },
    rpm: {
      status: 'pending',
      estimatedArrival: null,
    },
  });

  // Simulate Velocare integration auto-populating status
  useEffect(() => {
    if (disabled) return;

    // Calculate times from now - these will be in UTC
    // The display will convert them to the appropriate timezone
    const now = new Date();
    const suppliesDelivery = addHours(now, 2); // 2 hours for supplies
    const rpmArrival = addMinutes(now, 90); // 1.5 hours for RPM

    const newStatus: VelocareStatus = {
      supplies: {
        status: 'confirmed',
        estimatedDelivery: suppliesDelivery.toISOString(),
        items: ['IV Supplies', 'Medications', 'Wound Care Kit'],
      },
      rpm: {
        status: 'scheduled',
        estimatedArrival: rpmArrival.toISOString(),
      },
    };

    setStatus(newStatus);
    onStatusChange(newStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled]); // Only run when disabled changes - onStatusChange intentionally excluded to prevent infinite loop

  const getStatusColor = (
    status: string
  ): 'default' | 'success' | 'warning' | 'info' => {
    switch (status) {
      case 'delivered':
      case 'installed':
        return 'success';
      case 'confirmed':
      case 'scheduled':
      case 'in_transit':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string): string => {
    return status
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (disabled) {
    return (
      <Alert severity="info">
        Complete previous steps to check Velocare integration status.
      </Alert>
    );
  }

  // No loading state needed - data populates instantly

  return (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={2}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon color="success" />
          <Typography variant="h6">RPM/Supplies Vendor Integration</Typography>
          <Chip label="Auto-Updated" size="small" color="success" />
        </Box>

        {/* Supplies Status */}
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <LocalShippingIcon fontSize="small" />
            <Typography variant="subtitle2" fontWeight={600}>
              Supplies
            </Typography>
            <Chip
              label={getStatusLabel(status.supplies.status)}
              color={getStatusColor(status.supplies.status)}
              size="small"
            />
          </Stack>

          {status.supplies.estimatedDelivery && (
            <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
              Estimated Delivery:{' '}
              {timeLabelWithZone(status.supplies.estimatedDelivery, { ampm: true, timezone: patientTimezone })} today
            </Typography>
          )}

          {status.supplies.items.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
              Items: {status.supplies.items.join(', ')}
            </Typography>
          )}
        </Box>

        {/* RPM Status */}
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <CheckCircleIcon fontSize="small" />
            <Typography variant="subtitle2" fontWeight={600}>
              RPM Installation
            </Typography>
            <Chip
              label={getStatusLabel(status.rpm.status)}
              color={getStatusColor(status.rpm.status)}
              size="small"
            />
          </Stack>

          {status.rpm.estimatedArrival && (
            <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
              Estimated Installation:{' '}
              {timeLabelWithZone(status.rpm.estimatedArrival, { ampm: true, timezone: patientTimezone })} today
            </Typography>
          )}
        </Box>

        <Alert severity="success" icon={false}>
          <Typography variant="body2">
            <strong>Integration Status:</strong> Connected to vendor. Status updates automatically.
          </Typography>
        </Alert>
      </Stack>
    </Paper>
  );
}
